import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Wind, Droplets, Thermometer, MapPin, Home } from "lucide-react";

const LEVEL_STYLE = {
  none: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  clean: "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/40",
  med: "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/40",
  high: "bg-red-500/15 text-red-400 border-red-500/40",
  unknown: "bg-white/10 text-neutral-400 border-white/20",
};

export default function FieldWeather({ league }) {
  const { data, isLoading } = useQuery({
    queryKey: [league, "weather"],
    queryFn: () => api.get(`/football/weather?league=${league}`).then((r) => r.data),
    staleTime: 30 * 60 * 1000,
  });
  const games = data?.games || [];
  return (
    <div className="space-y-4">
      <p className="text-neutral-500 text-sm">Venue and game-time forecast for every matchup — domes flagged, wind/rain/cold edges called out. Weather moves totals; check here before you bet an over.</p>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Checking the skies...</div>}
      {!isLoading && games.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="fw-empty">No upcoming games on the slate.</div>}
      <div className="grid lg:grid-cols-2 gap-4">
        {games.map((g, i) => {
          const f = g.forecast;
          return (
            <div key={g.id} className="card-surface rounded-xl p-4 hover-lift" data-testid={`fw-game-${i}`}>
              <div className="flex items-center justify-between text-[11px] text-neutral-500 border-b border-white/10 pb-2 mb-3 gap-2">
                <span>{g.day_label} · {g.time_et}</span>
                <span className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border shrink-0 ${LEVEL_STYLE[g.impact?.level] || LEVEL_STYLE.unknown}`}>
                  {g.indoor ? "Dome" : g.impact?.level === "high" ? "Weather Game" : g.impact?.level === "med" ? "Watch" : "Outdoor"}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {g.away?.logo && <img src={g.away.logo} alt="" className="w-7 h-7 object-contain" loading="lazy" />}
                <span className="font-display font-bold uppercase text-sm">{g.away?.abbrev}</span>
                <span className="text-neutral-600 text-xs">@</span>
                {g.home?.logo && <img src={g.home.logo} alt="" className="w-7 h-7 object-contain" loading="lazy" />}
                <span className="font-display font-bold uppercase text-sm">{g.home?.abbrev}</span>
                <span className="text-[11px] text-neutral-500 inline-flex items-center gap-1 ml-auto"><MapPin size={11} className="text-[#a855f7]" /> {g.venue}{g.city ? ` · ${g.city}` : ""}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {g.indoor ? (
                  <span className="inline-flex items-center gap-1.5 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs font-data"><Home size={12} className="text-sky-300" /> Indoor / Dome</span>
                ) : f ? (
                  <>
                    <span className="inline-flex items-center gap-1.5 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs font-data" data-testid={`fw-temp-${i}`}><Thermometer size={12} className="text-[#a855f7]" /> {f.temp}°F</span>
                    <span className="inline-flex items-center gap-1.5 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs font-data"><Wind size={12} className="text-[#a855f7]" /> {f.wind_speed} mph {f.wind_dir}</span>
                    <span className="inline-flex items-center gap-1.5 bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs font-data"><Droplets size={12} className="text-sky-300" /> {f.precip}% rain</span>
                    <span className="bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs font-data text-neutral-400">{f.conditions}</span>
                  </>
                ) : g.espn?.cond ? (
                  <span className="bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs font-data text-neutral-400">{g.espn.cond}{g.espn.temp != null ? ` · ${g.espn.temp}°F` : ""}</span>
                ) : (
                  <span className="text-xs text-neutral-600 font-data">Forecast drops closer to kickoff</span>
                )}
              </div>
              <p className="text-[11px] text-neutral-400 mt-2.5">{g.impact?.note}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
